import { FormattedMessage, useIntl } from "@/i18n/i18n-client";
import styled from "styled-components";
import { Delete } from "@/assets/Icons/Delete";
import { rtl } from "@/shared/styles-utils";
import { useEffect, useState } from "react";
import { useStore } from "@/lib/storeData/StoreContext";
import {
  ProductAttributeValue,
  ProductCollection,
  useGetFiltersQuery,
  ProductOptionValue,
} from "@/generated/graphql";
import ClearAllFilters from "./ClearAllFilters";
import {
  FiltersDictionary,
  getAvailableFilters,
} from "@/shared/utils/filterHelperFunctions";
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from "@/i18n/i18n-navigation";
import {
  createSearchParams,
  parseSearchParams,
} from "@/shared/utils/search-params";

const FilterTags = () => {
  const [filtersDictionary, setFiltersDictionary] =
    useState<FiltersDictionary>();
  const router = useRouter();
  const searchparams = useSearchParams();
  const queries = parseSearchParams(searchparams);
  const params = useParams();
  const pathname = usePathname();
  const isCollectionPage = !!params.collection;
  const { storeId } = useStore();
  const intl = useIntl();
  const selectedFiltersIds =
    typeof queries.filters === "string"
      ? [queries.filters]
      : queries.filters || [];

  const { data, loading } = useGetFiltersQuery({
    variables: {
      storeId,
    },
  });

  useEffect(() => {
    if (data) {
      const { filtersDictionary: temp } = getAvailableFilters({
        attributesProductsDistribution: data?.getApplicableFilters
          ?.attributeValues! as ProductAttributeValue[],
        collectionsProductsDistribution: data?.getApplicableFilters
          ?.collections as ProductCollection[],
        optionsProductsDistribution: data?.getApplicableFilters
          ?.optionValues as ProductOptionValue[],
        intl,
      });
      setFiltersDictionary(temp);
    }
  }, [data, intl]);

  if (
    !(queries.filters || queries.minPrice || queries.maxPrice) ||
    isCollectionPage
  ) {
    return null;
  }
  const removeFilter = (filter: string) => {
    const newFilters = { ...queries };
    if (typeof newFilters.filters === "string") {
      newFilters.filters = [newFilters.filters];
    }

    if (filter === "minPrice" || filter === "maxPrice") {
      newFilters[filter] = undefined;
    } else {
      newFilters.filters = (newFilters.filters as string[])?.filter(
        (f: string) => f !== filter
      ) as string[];
    }
    if (
      !newFilters?.minPrice?.length &&
      !newFilters?.maxPrice?.length &&
      !newFilters?.filters?.length &&
      !queries.query
    ) {
      router.push("/shop");
      return;
    }
    const { query } = createSearchParams(newFilters);
    router.push(`${pathname}?${query}`, { scroll: false });
  };

  const getSelectedFiltersNames = () => {
    const selectedFiltersNames: {
      filterName: string;
      filterValue: string;
    }[] = [];

    if (!filtersDictionary) return [];

    for (const [, value] of Object.entries(filtersDictionary)) {
      value.forEach((filter) => {
        if (
          selectedFiltersIds.includes(filter.valueId) ||
          selectedFiltersIds?.includes(
            filter?.valueId! + "|" + filter?.propertyId!
          )
        )
          selectedFiltersNames.push({
            filterName: filter.valueName,
            filterValue: filter.valueId.startsWith("c:")
              ? filter.valueId
              : filter.valueId + "|" + filter.propertyId,
          });
      });
    }
    return selectedFiltersNames;
  };

  return (
    <TagsContainer>
      {queries.minPrice && (
        <FilterTag>
          <FormattedMessage defaultMessage="Min Price: " id="VCsdKk" />{" "}
          {queries.minPrice}
          <Div onClick={() => removeFilter("minPrice")}>
            <Delete />
          </Div>
        </FilterTag>
      )}
      {queries.maxPrice && (
        <FilterTag>
          <FormattedMessage defaultMessage="Max Price" id="xrO1Cn" />{" "}
          {queries.maxPrice}
          <Div onClick={() => removeFilter("maxPrice")}>
            <Delete />
          </Div>
        </FilterTag>
      )}
      {getSelectedFiltersNames().map((item) => (
        <FilterTag title={item.filterName} key={item.filterValue}>
          <FilterName>{item.filterName}</FilterName>
          <Div onClick={() => removeFilter(item.filterValue)}>
            <Delete />
          </Div>
        </FilterTag>
      ))}
      <ClearAllFilters loading={loading} plain />
    </TagsContainer>
  );
};
export default FilterTags;

const TagsContainer = styled.div`
  display: flex;
  margin-top: 30px;
  flex-wrap: wrap;
  width: 100vw;
  gap: 10px;
  margin-left: 20px;
  white-space: nowrap;
`;

const FilterTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafbfc;
  border: 1px solid #dfe3e8;
  border-radius: 4px;
  height: calc(100% - 2px);
  padding: 0 10px;
  color: #5f738c;
  font-size: 14px;
  text-overflow: ellipsis;
  padding: 10px;
  font-weight: 600;
  flex-direction: ${() => rtl("row-reverse", "row")};
  svg {
    margin-left: 5px;
    color: #252a31;
    stroke: #252a31;
    width: 10px;
  }
`;

const FilterName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 250px;
`;

const Div = styled.div`
  cursor: pointer;
`;
